import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, ProtectedRoute, useAuth } from "./lib/auth";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/Navbar";
import ActiveCompanyRoute from "./components/ActiveCompanyRoute";
import { initAnalytics, trackPage } from "./lib/analytics";

initAnalytics();

import Landing from "./pages/Landing";
import Privacy from "./pages/Privacy";
import FeatureMockInterview from "./pages/features/MockInterview";
import FeatureCustomInterview from "./pages/features/CustomInterview";
import FeatureResumeAnalyzer from "./pages/features/ResumeAnalyzer";
import FeatureAutopilot from "./pages/features/Autopilot";
import SolutionAiScreening from "./pages/solutions/AiScreening";
import SolutionProctoring from "./pages/solutions/Proctoring";
import SolutionVideoInterviews from "./pages/solutions/VideoInterviews";
import SolutionTalentPool from "./pages/solutions/TalentPool";
import SolutionCompliance from "./pages/solutions/Compliance";
import SolutionIntegrations from "./pages/solutions/Integrations";
import BookDemo from "./pages/BookDemo";
import ApplyProfile from "./pages/ApplyProfile";
import ApplyTracker from "./pages/ApplyTracker";
import ApplyStatus from "./pages/ApplyStatus";
import ApplyInbox from "./pages/ApplyInbox";
import AIApplyHub from "./pages/AIApplyHub";
import AutopilotSetup from "./pages/AutopilotSetup";
import ApplyConfirm from "./pages/ApplyConfirm";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import NewInterview from "./pages/NewInterview";
import LiveInterview from "./pages/LiveInterview";
import Results from "./pages/Results";
import NewJob from "./pages/NewJob";
import JobCandidates from "./pages/JobCandidates";
import CompanyRoles from "./pages/CompanyRoles";
import CompanyRoleNew from "./pages/CompanyRoleNew";
import CompanyRoleDashboard from "./pages/CompanyRoleDashboard";
import CompanyRoleEdit from "./pages/CompanyRoleEdit";
import CompanyBilling from "./pages/CompanyBilling";
import Admin from "./pages/Admin";
import AdminUsers from "./pages/AdminUsers";
import AdminCandidates from "./pages/AdminCandidates";
import AdminCompanies from "./pages/AdminCompanies";
import AdminCompanyDetail from "./pages/AdminCompanyDetail";
import AdminCandidateUsers from "./pages/AdminCandidateUsers";
import AdminRoles from "./pages/AdminRoles";
import AdminSeedCatalog from "./pages/AdminSeedCatalog";
import AdminRoleRequests from "./pages/AdminRoleRequests";
import AdminRoleDetail from "./pages/AdminRoleDetail";
import AdminIndustries from "./pages/AdminIndustries";
import AdminFeedback from "./pages/AdminFeedback";
import AdminWorkdayTenants from "./pages/AdminWorkdayTenants";
import AdminAtsSetups from "./pages/AdminAtsSetups";
import AdminAtsDiagnostics from "./pages/AdminAtsDiagnostics";
import Support from "./pages/Support";
import AdminSupport from "./pages/AdminSupport";
import AdminJobSupply from "./pages/AdminJobSupply";
import AdminAtsAutomation from "./pages/AdminAtsAutomation";
import AdminPromoCodes from "./pages/AdminPromoCodes";
import CustomRoles from "./pages/CustomRoles";
import CustomRoleDetail from "./pages/CustomRoleDetail";
import ResumeAnalyzer from "./pages/ResumeAnalyzer";
import ResumesHub from "./pages/ResumesHub";
import AutopilotJobs from "./pages/AutopilotJobs";
import Profile from "./pages/Profile";
import Subscription from "./pages/Subscription";
import ApplyPage from "./pages/ApplyPage";
import CompanyConsentSettings from "./pages/CompanyConsentSettings";
import TalentPoolPage from "./pages/TalentPoolPage";
import CompanyEmployees from "./pages/CompanyEmployees";
import CompanyIntegrations from "./pages/CompanyIntegrations";
import ChangePassword from "./pages/ChangePassword";
import AutoInterview from "./pages/AutoInterview";

function Shell({ children }) {
  const location = useLocation();
  // The AI Apply hub lives under /apply and DOES show the nav; only the focused
  // /apply/:roleId apply flow (and interview flows) hide it.
  const applyHubPaths = ["/apply", "/apply/tracker", "/apply/settings"];
  const inApplyHub = applyHubPaths.includes(location.pathname);
  const hideNav = !inApplyHub && (
    location.pathname.startsWith("/interview/") || location.pathname.startsWith("/i/") ||
    location.pathname.startsWith("/apply/") || location.pathname.startsWith("/auto-interview/") ||
    location.pathname.startsWith("/ai/")
  );

  useEffect(() => {
    trackPage(location.pathname);
  }, [location.pathname]);

  return (
    <>
      {!hideNav && <Navbar />}
      {children}
      <Toaster position="top-right" />
    </>
  );
}

function AppRouter() {
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Landing />} />
        {/* PUBLIC on purpose: the Chrome Web Store fetches this during review and the
            reviewer has no account, so it must never sit behind ProtectedRoute. */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/features/mock-interview" element={<FeatureMockInterview />} />
        <Route path="/features/custom-interview" element={<FeatureCustomInterview />} />
        <Route path="/features/resume-analyzer" element={<FeatureResumeAnalyzer />} />
        <Route path="/features/autopilot" element={<FeatureAutopilot />} />
        <Route path="/solutions/ai-screening" element={<SolutionAiScreening />} />
        <Route path="/solutions/proctoring" element={<SolutionProctoring />} />
        <Route path="/solutions/video-interviews" element={<SolutionVideoInterviews />} />
        <Route path="/solutions/talent-pool" element={<SolutionTalentPool />} />
        <Route path="/solutions/compliance" element={<SolutionCompliance />} />
        <Route path="/solutions/integrations" element={<SolutionIntegrations />} />
        <Route path="/book-demo" element={<BookDemo />} />
        <Route path="/i/:token" element={<AutoInterview />} />
        <Route path="/ai/:token" element={<AutoInterview />} />
        <Route path="/auto-interview/:id" element={<ProtectedRoute role="candidate"><AutoInterview /></ProtectedRoute>} />
        {/* Public one-tap apply confirmation (from the email deep-link, token-authenticated) */}
        <Route path="/apply/confirm" element={<ApplyConfirm />} />
        {/* Autopilot first-run setup — full-screen wizard (nav auto-hidden). Must precede /apply/:roleId. */}
        <Route path="/apply/setup" element={<ProtectedRoute role="candidate"><AutopilotSetup /></ProtectedRoute>} />
        {/* Per-job status page + global application inbox (focused pages; nav auto-hidden). Must precede /apply/:roleId. */}
        <Route path="/apply/status/:draftId" element={<ProtectedRoute role="candidate"><ApplyStatus /></ProtectedRoute>} />
        <Route path="/support" element={<ProtectedRoute role="candidate"><Support /></ProtectedRoute>} />
        <Route path="/admin/job-supply" element={<ProtectedRoute role="admin"><AdminJobSupply /></ProtectedRoute>} />
        <Route path="/admin/ats-automation" element={<ProtectedRoute role="admin"><AdminAtsAutomation /></ProtectedRoute>} />
        <Route path="/admin/support" element={<ProtectedRoute role="admin"><AdminSupport /></ProtectedRoute>} />
        <Route path="/apply/inbox" element={<ProtectedRoute role="candidate"><ApplyInbox /></ProtectedRoute>} />
        <Route path="/apply/:roleId" element={<ApplyPage />} />
<Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/interviews/new" element={<ProtectedRoute role="candidate"><NewInterview /></ProtectedRoute>} />
        <Route path="/interview/:id" element={<ProtectedRoute role="candidate"><LiveInterview /></ProtectedRoute>} />
        <Route path="/interviews/:id/results" element={<ProtectedRoute><Results /></ProtectedRoute>} />
        <Route path="/jobs/new" element={<ProtectedRoute role="company"><ActiveCompanyRoute><NewJob /></ActiveCompanyRoute></ProtectedRoute>} />
        <Route path="/jobs/:id/candidates" element={<ProtectedRoute role="company"><ActiveCompanyRoute><JobCandidates /></ActiveCompanyRoute></ProtectedRoute>} />
        <Route path="/company/roles" element={<ProtectedRoute role="company"><ActiveCompanyRoute><CompanyRoles /></ActiveCompanyRoute></ProtectedRoute>} />
        <Route path="/company/roles/new" element={<ProtectedRoute role="company"><ActiveCompanyRoute><CompanyRoleNew /></ActiveCompanyRoute></ProtectedRoute>} />
        <Route path="/company/consent" element={<ProtectedRoute role="company"><ActiveCompanyRoute><CompanyConsentSettings /></ActiveCompanyRoute></ProtectedRoute>} />
        <Route path="/company/talent-pool" element={<ProtectedRoute role="company"><ActiveCompanyRoute><TalentPoolPage /></ActiveCompanyRoute></ProtectedRoute>} />
        <Route path="/company/employees" element={<ProtectedRoute role="company"><ActiveCompanyRoute><CompanyEmployees /></ActiveCompanyRoute></ProtectedRoute>} />
        <Route path="/company/integrations" element={<ProtectedRoute role="company"><ActiveCompanyRoute><CompanyIntegrations /></ActiveCompanyRoute></ProtectedRoute>} />
        <Route path="/company/roles/:id" element={<ProtectedRoute role="company"><ActiveCompanyRoute><CompanyRoleDashboard /></ActiveCompanyRoute></ProtectedRoute>} />
        <Route path="/company/roles/:id/edit" element={<ProtectedRoute role="company"><ActiveCompanyRoute><CompanyRoleEdit /></ActiveCompanyRoute></ProtectedRoute>} />
        {/* Billing is reachable even when past_due/pending so admins can fix payment — not wrapped in ActiveCompanyRoute */}
        <Route path="/company/billing" element={<ProtectedRoute role="company"><CompanyBilling /></ProtectedRoute>} />
        <Route path="/change-password" element={<ProtectedRoute><ChangePassword forced /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute role="admin"><Admin /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute role="admin"><AdminUsers /></ProtectedRoute>} />
        <Route path="/admin/candidates" element={<ProtectedRoute role="admin"><AdminCandidates /></ProtectedRoute>} />
        <Route path="/admin/companies" element={<ProtectedRoute role="admin"><AdminCompanies /></ProtectedRoute>} />
        <Route path="/admin/companies/:id" element={<ProtectedRoute role="admin"><AdminCompanyDetail /></ProtectedRoute>} />
        <Route path="/admin/candidate-users" element={<ProtectedRoute role="admin"><AdminCandidateUsers /></ProtectedRoute>} />
        <Route path="/admin/roles" element={<ProtectedRoute role="admin"><AdminRoles /></ProtectedRoute>} />
        <Route path="/admin/seed" element={<ProtectedRoute role="admin"><AdminSeedCatalog /></ProtectedRoute>} />
        <Route path="/admin/role-requests" element={<ProtectedRoute role="admin"><AdminRoleRequests /></ProtectedRoute>} />
        <Route path="/admin/roles/:roleId" element={<ProtectedRoute role="admin"><AdminRoleDetail /></ProtectedRoute>} />
        <Route path="/admin/industries" element={<ProtectedRoute role="admin"><AdminIndustries /></ProtectedRoute>} />
        <Route path="/admin/feedback" element={<ProtectedRoute role="admin"><AdminFeedback /></ProtectedRoute>} />
        <Route path="/admin/workday-tenants" element={<ProtectedRoute role="admin"><AdminWorkdayTenants /></ProtectedRoute>} />
        <Route path="/admin/ats-setups" element={<ProtectedRoute role="admin"><AdminAtsSetups /></ProtectedRoute>} />
        <Route path="/admin/ats-diagnostics" element={<ProtectedRoute role="admin"><AdminAtsDiagnostics /></ProtectedRoute>} />
        <Route path="/admin/promo-codes" element={<ProtectedRoute role="admin"><AdminPromoCodes /></ProtectedRoute>} />
        <Route path="/custom-roles" element={<ProtectedRoute role="candidate"><CustomRoles /></ProtectedRoute>} />
        <Route path="/custom-roles/:roleId" element={<ProtectedRoute role="candidate"><CustomRoleDetail /></ProtectedRoute>} />
        <Route path="/resume-analyzer" element={<ProtectedRoute role="candidate"><ResumeAnalyzer /></ProtectedRoute>} />
        <Route path="/resumes" element={<ProtectedRoute role="candidate"><ResumesHub /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute role="candidate"><Profile /></ProtectedRoute>} />
        {/* AI Apply hub — one workspace, three tabs: AutoPilot Jobs (default) / Application Tracker / Settings */}
        <Route path="/apply" element={<ProtectedRoute role="candidate"><AIApplyHub /></ProtectedRoute>}>
          <Route index element={<AutopilotJobs />} />
          <Route path="tracker" element={<ApplyTracker />} />
          <Route path="settings" element={<ApplyProfile />} />
        </Route>
        {/* Redirects from the old standalone routes into the hub */}
        {/* Job alerts were removed (2026-10-03); old bookmarks land on the matches grid. */}
        <Route path="/job-alerts/*" element={<Navigate to="/apply" replace />} />
        <Route path="/apply/profile" element={<Navigate to="/apply/settings" replace />} />
        <Route path="/subscription" element={<ProtectedRoute role="candidate"><Subscription /></ProtectedRoute>} />
      </Routes>
    </Shell>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
